/**
 * Marcação por critério ancorada à assinatura contestada (E23.F10 / ADR-0027). // ux-neutrality:allow doc-comment
 *
 * Desenha um SVG sobre a imagem da contestada com um marcador por evidência de // ux-neutrality:allow doc-comment
 * critério LOCALIZÁVEL (centróide de mínimo, endpoint, laço, vão, zona). A COR // ux-neutrality:allow doc-comment
 * vem do status do critério resolvido pelo backend (`criteria_status`, banda // ux-neutrality:allow doc-comment
 * canônica [1/3, 3] — E25.F3/ADR-0035) — o front não deriva status de LR. // ux-neutrality:allow doc-comment
 * O SVG usa viewBox = dimensões naturais da imagem e // ux-neutrality:allow doc-comment
 * escala junto com o <img> (mesmo box, inset-0). // ux-neutrality:allow doc-comment
 *
 * Honestidade (ADR-0027 §2C): o marcador localiza a evidência do critério, não // ux-neutrality:allow doc-comment
 * afirma que aquele traço é falso — o veredito é do critério como um todo. // ux-neutrality:allow doc-comment
 *
 * E36.F1 (round 4 do escritório): todos os marcadores são círculos de // ux-neutrality:allow doc-comment
 * CONTORNO (fill none, traço fino) — sem preenchimento nem retângulos; zonas // ux-neutrality:allow doc-comment
 * viram círculo no centróide. O overlay vem OCULTO por padrão (toggle no // ux-neutrality:allow doc-comment
 * ContestedCanvasTools). Mesmo estilo no laudo (_draw_localizations/PIL). // ux-neutrality:allow doc-comment
 */
import { ptBR } from '@/i18n/pt-BR';
import type { CriterionBandStatus, QuestionedLocalizationsData } from '@/lib/case_workspace';
import { ANNOTATION_COLOR_HEX } from '@/lib/mesa_annotations';
import {
  criterionDisplayName,
  criterionStatusFrom,
  type DerivedStatus,
} from '@/lib/criterion_status';

export interface ContestedMarkerOverlayProps {
  localizations: QuestionedLocalizationsData;
  /** E25.F3/ADR-0035: status por critério resolvido pelo backend. */
  criteriaStatus: Record<string, CriterionBandStatus> | null;
}

/**
 * E54.F5.T01: a cor do marcador vem da rampa de sinal do Design System, pelo
 * mesmo mapa das anotações do perito — a mesa tem uma paleta de sinal só.
 */
const _STATUS_COLOR: Record<DerivedStatus, string> = {
  convergent: ANNOTATION_COLOR_HEX.green,
  divergent: ANNOTATION_COLOR_HEX.red,
  neutral: ANNOTATION_COLOR_HEX.amber,
};

export function ContestedMarkerOverlay({
  localizations,
  criteriaStatus,
}: ContestedMarkerOverlayProps): JSX.Element {
  const { image_width, image_height, criteria } = localizations;
  // Raio do ponto proporcional ao tamanho da imagem (legível em qualquer escala).
  const radius = Math.max(4, Math.round(Math.max(image_width, image_height) * 0.012));
  // E36.F1: traço fino (~2px na escala natural da imagem) — pedido do
  // escritório: marcação discreta que não cobre o traço da assinatura.
  const stroke = Math.max(1.5, Math.round(Math.max(image_width, image_height) * 0.0025));

  return (
    <svg
      className="contested-markers"
      viewBox={`0 0 ${image_width} ${image_height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ptBR.workspaceCaso.markersOverlayLabel}
      data-testid="contested-marker-overlay"
    >
      {criteria.map((crit) => {
        const status = criterionStatusFrom(criteriaStatus, crit.criterion_key);
        const color = _STATUS_COLOR[status];
        const name = criterionDisplayName(crit.criterion_key);
        return (
          <g
            key={crit.criterion_key}
            data-testid={`marker-group-${crit.criterion_key}`}
            data-status={status}
          >
            {crit.markers.map((m, idx) => {
              const title = `${name}`;
              // E36.F1: TODO marcador é círculo de contorno, sem preenchimento
              // e sem retângulos/fundos. Zona (region) vira círculo no
              // centróide com raio derivado do tamanho da região.
              let cx = m.x;
              let cy = m.y;
              let r = radius;
              if (m.kind === 'region' && m.width !== null && m.height !== null) {
                cx = m.x + m.width / 2;
                cy = m.y + m.height / 2;
                r = Math.max(radius, Math.round(Math.min(m.width, m.height) / 2));
              }
              return (
                <circle
                  key={idx}
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill="none"
                  stroke={color}
                  strokeWidth={stroke}
                >
                  <title>{title}</title>
                </circle>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}
